/**
 * Determines if a strategy targets providers (HCPs) or sites (facilities).
 *
 * This is the single source of truth for provider/site focus. Both the
 * optimization parameters step (which syncs the result into Redux) and the
 * comparison view rely on it so the "Providers" / "Sites" labeling stays
 * consistent across every view of the same job.
 *
 * - FreshFaces/CodeSet: check `provider_type` ("hcp"/"provider" = providers,
 *   "site" = sites)
 * - ScoutList: extract `query_type` from the first `query_plan` step
 * - Indeterminate: returns `null` so callers can label the entity count
 *   generically ("Entities") rather than guessing a type we can't confirm.
 */
const getIsProviderFocused = (strategyDetails: any): boolean | null => {
  if (!strategyDetails) return null;

  // FreshFaces and CodeSet have an explicit provider_type field
  if (strategyDetails.provider_type) {
    const providerType = strategyDetails.provider_type.toLowerCase();
    return providerType === 'hcp' || providerType === 'provider';
  }

  // ScoutList: extract from query_plan steps
  if (strategyDetails.query_plan?.steps?.length > 0) {
    const firstStep = strategyDetails.query_plan.steps[0];
    const queryType = firstStep?.params?.query_type;
    if (queryType) {
      return queryType.toLowerCase() !== 'site';
    }
  }

  // Can't determine focus from the strategy shape.
  return null;
};

export default getIsProviderFocused;
